import {
  AfterContentInit, Component, ContentChildren, Input, Optional, QueryList
} from '@angular/core';
import {
  ControlContainer,
  FormArray,
  FormArrayName,
  FormGroup,
  FormGroupName,
  ValidationErrors
} from '@angular/forms';

import { IrisCheckboxComponent } from '@sondermind/iris/checkbox';
import { IrisDropdownComponent } from '@sondermind/iris/dropdown';
import { IIrisFormFieldControl } from '@sondermind/iris/form-helpers';
import { IrisRadioGroupComponent } from '@sondermind/iris/radio-group';
import { IrisTextAreaComponent } from '@sondermind/iris/text-area';
import { IrisTextInputComponent } from '@sondermind/iris/text-input';

import { FormControlType } from './enums/form-control-type.enum';

@Component({
  selector: 'iris-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  standalone: false
})

export class IrisFormFieldComponent implements AfterContentInit {
  /* Used to specify the error messages to display for any potential validation error type. */
  @Input() customErrorMessages: { [ key: string ]: string; } = {};
  @Input() label: string = '';
  @Input() helperText: string = '';
  @Input() showOptionalTag: boolean = true;

  @ContentChildren(IrisRadioGroupComponent) irisRadioGroupChildren!: QueryList<IrisRadioGroupComponent<unknown>>;
  @ContentChildren(IrisTextAreaComponent) irisTextAreaChildren!: QueryList<IrisTextAreaComponent>;
  @ContentChildren(IrisTextInputComponent) irisTextInputChildren!: QueryList<IrisTextInputComponent>;
  @ContentChildren(IrisDropdownComponent) irisDropdownChildren!: QueryList<IrisDropdownComponent<unknown>>;

  /* Used to pass in multiple checkboxes */
  @ContentChildren(FormGroupName) formGroupChildren!: QueryList<FormGroupName>;
  @ContentChildren(FormArrayName) formArrayChildren!: QueryList<FormArrayName>;
  // eslint-disable-next-line max-len
  @ContentChildren(IrisCheckboxComponent, { descendants: true }) irisCheckboxChildren!: QueryList<IrisCheckboxComponent>;

  /* Used to set label's "for" attribute on single field */
  controlName: string = '';
  controlType!: FormControlType;
  irisFormFieldControl?: IIrisFormFieldControl;
  /* Determines whether to use the <fieldset> and <legend> elements in the HTML */
  useFieldSet: boolean = false;

  /* Set default error messages here. Custom messages can be set with
  /* the `customErrorMessages` Input */
  errorMessageStrings: { [key: string]: string; } = {
    required: 'This field is required.',
    maxlength: 'Response is too long. Please shorten it.',
    maxOptions: 'Too many items selected.'
  };

  constructor(@Optional() private controlContainer: ControlContainer) {}

  /* An iris-form-field may contain:
   * 1 Radiogroup (should use fieldset)
   * 1 textarea (should not use fieldset)
   * 1 text input (should not use fieldset)
   * 1 checkbox (should use fieldset)
   * >1 checkboxes (should use fieldset, and be passed through a formGroup)
   * 1 dropdown (should use fieldset) */

  ngAfterContentInit(): void {
    this.setCustomErrorMessages(this.customErrorMessages);

    this.controlType = this.getControlType();

    this.useFieldSet = this.controlType === FormControlType.CHECKBOX_MULTI
      || this.controlType === FormControlType.CHECKBOX_SINGLE
      || this.controlType === FormControlType.DROPDOWN
      || this.controlType === FormControlType.RADIO_GROUP;

    if (this.controlType === FormControlType.INVALID) {
      throw new Error('Invalid content between iris-form-field tags');
    } else if (this.controlType === FormControlType.CHECKBOX_MULTI) {
      this.initSubForm();
    } else {
      this.initSingleField();
    }
  }

  private initSubForm(): void {
    // for every child checkbox, set the subFormControl value to true to handle a11y error association
    this.irisCheckboxChildren.forEach((control) => {
      // eslint-disable-next-line no-param-reassign
      control.subFormControl = true;
    });

    if (this.formGroupChildren?.length < 1 && this.formArrayChildren?.length < 1) {
      throw new Error(
        'Ensure multiple checkboxes are contained within an element using the FormGroupName or FormGroupArray directive'
      );
    }
  }

  private initSingleField(): void {
    const controlMapping: { [key: string]: IIrisFormFieldControl; } = {
      [FormControlType.CHECKBOX_SINGLE]: this.irisCheckboxChildren.first,
      [FormControlType.DROPDOWN]: this.irisDropdownChildren.first,
      [FormControlType.RADIO_GROUP]: this.irisRadioGroupChildren.first,
      [FormControlType.TEXT_INPUT]: this.irisTextInputChildren.first,
      [FormControlType.TEXT_AREA]: this.irisTextAreaChildren.first,
    };

    this.irisFormFieldControl = controlMapping[this.controlType];

    if (this.irisFormFieldControl) {
      this.controlName = this.irisFormFieldControl.formControlName;
    }
  }

  private getControlType(): FormControlType {
    const controlConditions = [
      { condition: this.irisCheckboxChildren?.length > 1, type: FormControlType.CHECKBOX_MULTI },
      { condition: this.irisCheckboxChildren?.length === 1, type: FormControlType.CHECKBOX_SINGLE },
      { condition: this.irisDropdownChildren?.length > 0, type: FormControlType.DROPDOWN },
      { condition: this.irisRadioGroupChildren?.length > 0, type: FormControlType.RADIO_GROUP },
      { condition: this.irisTextInputChildren?.length > 0, type: FormControlType.TEXT_INPUT },
      { condition: this.irisTextAreaChildren?.length > 0, type: FormControlType.TEXT_AREA },
    ];

    const matchedControl = controlConditions.find(({ condition }) => condition);
    return matchedControl ? matchedControl.type : FormControlType.INVALID;
  }

  private getErrorMessages(errors: ValidationErrors | null): string[] {
    return errors
      ? Object.keys(errors)
        .map((error): string => {
          const message = this.errorMessageStrings[error];
          return message || (typeof errors[error] === 'string' ? (errors[error] as string) : '');
        })
        .filter((message): message is string => message !== '')
      : [];
  }

  private setCustomErrorMessages(customErrorMessages: { [ key: string ]: string; }): void {
    /* Set custom error messages. */
    Object.keys(customErrorMessages).forEach((error) => {
      this.errorMessageStrings[error] = customErrorMessages[error];
    });
  }

  /* The below getters are used because the current FormControl and/or FormGroup
    may have changed since the form-field was initialized */

  get multiCheckboxSubForm(): FormGroup | FormArray | null {
    if (this.controlType === FormControlType.CHECKBOX_MULTI) {
      if (this.formGroupChildren?.length > 0) {
        return this.getSubFormGroup();
      }

      if (this.formArrayChildren?.length > 0) {
        return this.getSubFormArray();
      }
    }

    return null;
  }

  get errorMessagesToDisplay(): string[] {
    if (this.controlType === FormControlType.CHECKBOX_MULTI) {
      const subForm = this.multiCheckboxSubForm;

      if (this.formGroupChildren?.length > 0) {
        return this.getSubFormGroupErrors(subForm as FormGroup);
      }

      if (this.formArrayChildren?.length > 0) {
        return this.getSubFormArrayErrors(subForm as FormArray);
      }
    }

    const errors = this.irisFormFieldControl
      ? this.irisFormFieldControl.control.errors
      : null;

    return this.getErrorMessages(errors);
  }

  private getSubFormGroup(): FormGroup {
    const parentFormGroup = this.controlContainer.control as FormGroup;
    const subFormGroupName = (this.formGroupChildren.first.name ?? '').toString();
    return parentFormGroup.get(subFormGroupName) as FormGroup;
  }

  private getSubFormArray(): FormArray {
    const parentFormArray = this.controlContainer.control as FormArray;
    const subFormArrayName = (this.formArrayChildren.first.name ?? '').toString();
    return parentFormArray.get(subFormArrayName) as FormArray;
  }

  private getSubFormGroupErrors(subForm: FormGroup): string[] {
    /* Add subform-specific error messages. */
    return this.getErrorMessages(subForm.errors)
    /* Add control-specific error messages. */
      .concat(...Object
        .values(subForm.controls)
        .map((control) => this.getErrorMessages(control.errors)));
  }

  private getSubFormArrayErrors(subForm: FormArray): string[] {
    /* Add subform-specific error messages. */
    return this.getErrorMessages(subForm.errors)
    /* Add control-specific error messages. */
      .concat(...subForm.controls
        .map((control) => this.getErrorMessages(control.errors)));
  }
}
